'use client';
import React, { useState, useCallback } from 'react';
import { ArrowLeft, RotateCcw, Trophy } from 'lucide-react';

interface Props { onBack: () => void; }

type PieceType = 'K' | 'Q' | 'R' | 'B' | 'N' | 'P';
type Color = 'w' | 'b';
interface Piece { type: PieceType; color: Color; }
type Square = Piece | null;
type Board = Square[][];

const PIECE_UNICODE: Record<PieceType, Record<Color, string>> = {
  K: { w: '♔', b: '♚' }, Q: { w: '♕', b: '♛' },
  R: { w: '♖', b: '♜' }, B: { w: '♗', b: '♝' },
  N: { w: '♘', b: '♞' }, P: { w: '♙', b: '♟' },
};

const LEADERBOARD = [
  { name: 'Magnus A.', wins: 42, elo: 2480, score: 9800 },
  { name: 'Arjun K.', wins: 31, elo: 2210, score: 7200 },
  { name: 'Priya S.', wins: 28, elo: 2050, score: 6500 },
  { name: 'Marcus T.', wins: 19, elo: 1890, score: 4400 },
  { name: 'Yuki H.', wins: 15, elo: 1720, score: 3500 },
];

function createInitialBoard(): Board {
  const board: Board = Array(8).fill(null).map(() => Array(8).fill(null));
  const backRank: PieceType[] = ['R','N','B','Q','K','B','N','R'];
  backRank.forEach((type, col) => {
    board[0][col] = { type, color: 'b' };
    board[7][col] = { type, color: 'w' };
  });
  for (let col = 0; col < 8; col++) {
    board[1][col] = { type: 'P', color: 'b' };
    board[6][col] = { type: 'P', color: 'w' };
  }
  return board;
}

function getValidMoves(board: Board, row: number, col: number): [number, number][] {
  const piece = board[row][col];
  if (!piece) return [];
  const moves: [number, number][] = [];
  const inBounds = (r: number, c: number) => r >= 0 && r < 8 && c >= 0 && c < 8;
  const canCapture = (r: number, c: number) => inBounds(r, c) && board[r][c]?.color !== piece.color;
  const isEmpty = (r: number, c: number) => inBounds(r, c) && !board[r][c];
  const addSlide = (dr: number, dc: number) => {
    let r = row + dr, c = col + dc;
    while (inBounds(r, c)) {
      if (board[r][c]) { if (board[r][c]!.color !== piece.color) moves.push([r, c]); break; }
      moves.push([r, c]); r += dr; c += dc;
    }
  };

  switch (piece.type) {
    case 'P': {
      const dir = piece.color === 'w' ? -1 : 1;
      const startRow = piece.color === 'w' ? 6 : 1;
      if (isEmpty(row + dir, col)) {
        moves.push([row + dir, col]);
        if (row === startRow && isEmpty(row + 2 * dir, col)) moves.push([row + 2 * dir, col]);
      }
      if (inBounds(row + dir, col - 1) && board[row + dir][col - 1]?.color !== piece.color && board[row + dir][col - 1]) moves.push([row + dir, col - 1]);
      if (inBounds(row + dir, col + 1) && board[row + dir][col + 1]?.color !== piece.color && board[row + dir][col + 1]) moves.push([row + dir, col + 1]);
      break;
    }
    case 'R': addSlide(1,0); addSlide(-1,0); addSlide(0,1); addSlide(0,-1); break;
    case 'B': addSlide(1,1); addSlide(1,-1); addSlide(-1,1); addSlide(-1,-1); break;
    case 'Q': addSlide(1,0); addSlide(-1,0); addSlide(0,1); addSlide(0,-1); addSlide(1,1); addSlide(1,-1); addSlide(-1,1); addSlide(-1,-1); break;
    case 'N':
      [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]].forEach(([dr,dc]) => {
        if (canCapture(row+dr, col+dc)) moves.push([row+dr, col+dc]);
      }); break;
    case 'K':
      [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]].forEach(([dr,dc]) => {
        if (canCapture(row+dr, col+dc)) moves.push([row+dr, col+dc]);
      }); break;
  }
  return moves;
}

export default function ChessGame({ onBack }: Props) {
  const [board, setBoard] = useState<Board>(createInitialBoard);
  const [selected, setSelected] = useState<[number, number] | null>(null);
  const [validMoves, setValidMoves] = useState<[number, number][]>([]);
  const [currentTurn, setCurrentTurn] = useState<Color>('w');
  const [capturedW, setCapturedW] = useState<Piece[]>([]);
  const [capturedB, setCapturedB] = useState<Piece[]>([]);
  const [moveCount, setMoveCount] = useState(0);
  const [gameStatus, setGameStatus] = useState<'playing' | 'checkmate' | 'stalemate'>('playing');
  const [xp, setXp] = useState(0);
  const [moveHistory, setMoveHistory] = useState<string[]>([]);

  const handleSquareClick = useCallback((row: number, col: number) => {
    if (gameStatus !== 'playing') return;
    const piece = board[row][col];

    if (selected) {
      const isValid = validMoves.some(([r, c]) => r === row && c === col);
      if (isValid) {
        const newBoard = board.map(r => [...r]);
        const movingPiece = newBoard[selected[0]][selected[1]]!;
        const captured = newBoard[row][col];
        if (captured) {
          if (captured.color === 'w') setCapturedW(prev => [...prev, captured]);
          else setCapturedB(prev => [...prev, captured]);
          setXp(prev => prev + 50);
          if (captured.type === 'K') {
            setGameStatus('checkmate');
            setXp(prev => prev + 500);
          }
        }
        // Pawn promotion
        if (movingPiece.type === 'P' && (row === 0 || row === 7)) {
          newBoard[row][col] = { type: 'Q', color: movingPiece.color };
        } else {
          newBoard[row][col] = movingPiece;
        }
        newBoard[selected[0]][selected[1]] = null;
        const files = 'abcdefgh';
        const ranks = '87654321';
        const notation = `${PIECE_UNICODE[movingPiece.type][movingPiece.color]}${files[selected[1]]}${ranks[selected[0]]}→${files[col]}${ranks[row]}`;
        setMoveHistory(prev => [notation, ...prev].slice(0, 10));
        setBoard(newBoard);
        setCurrentTurn(currentTurn === 'w' ? 'b' : 'w');
        setMoveCount(m => m + 1);
        setXp(prev => prev + 10);
      }
      setSelected(null);
      setValidMoves([]);
    } else if (piece && piece.color === currentTurn) {
      setSelected([row, col]);
      setValidMoves(getValidMoves(board, row, col));
    }
  }, [board, selected, validMoves, currentTurn, gameStatus]);

  const resetGame = useCallback(() => {
    setBoard(createInitialBoard());
    setSelected(null);
    setValidMoves([]);
    setCurrentTurn('w');
    setCapturedW([]);
    setCapturedB([]);
    setMoveCount(0);
    setGameStatus('playing');
    setMoveHistory([]);
  }, []);

  const isSelected = (r: number, c: number) => selected?.[0] === r && selected?.[1] === c;
  const isValidMove = (r: number, c: number) => validMoves.some(([vr, vc]) => vr === r && vc === c);

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="btn-ghost p-2 rounded-lg"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">♟️ Chess</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Classic strategy — checkmate to win</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={resetGame} className="btn-ghost flex items-center gap-2 text-sm">
            <RotateCcw size={16} /> New Game
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Status */}
          <div className="rounded-xl p-3 mb-4 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {gameStatus === 'checkmate' ? (
              <p className="font-bold text-lg" style={{ color: '#10B981' }}>
                🏆 {currentTurn === 'w' ? 'Black' : 'White'} wins by checkmate!
              </p>
            ) : (
              <p className="font-semibold text-foreground">
                <span style={{ color: currentTurn === 'w' ? '#F1F5F9' : '#94A3B8' }}>
                  {currentTurn === 'w' ? '⬜ White' : '⬛ Black'}
                </span>
                &apos;s turn · Move {moveCount + 1}
              </p>
            )}
          </div>

          {/* Captured pieces */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { label: 'Captured by White', pieces: capturedB, color: '#F1F5F9' },
              { label: 'Captured by Black', pieces: capturedW, color: '#374151' },
            ].map((side) => (
              <div key={side.label} className="rounded-xl p-3" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{side.label}</p>
                <div className="flex flex-wrap gap-1 min-h-[24px]">
                  {side.pieces.map((p, i) => (
                    <span key={i} className="text-lg">{PIECE_UNICODE[p.type][p.color]}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Chess board */}
          <div className="flex justify-center">
            <div className="rounded-xl overflow-hidden" style={{ border: '3px solid var(--border)' }}>
              {board.map((row, rIdx) => (
                <div key={rIdx} className="flex">
                  {row.map((piece, cIdx) => {
                    const isLight = (rIdx + cIdx) % 2 === 0;
                    const isSel = isSelected(rIdx, cIdx);
                    const isVM = isValidMove(rIdx, cIdx);
                    const hasEnemy = isVM && !!piece;
                    return (
                      <button
                        key={cIdx}
                        onClick={() => handleSquareClick(rIdx, cIdx)}
                        className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-2xl sm:text-3xl relative transition-all duration-100"
                        style={{
                          background: isSel
                            ? 'rgba(124,58,237,0.6)'
                            : hasEnemy
                            ? 'rgba(244,63,94,0.5)'
                            : isLight
                            ? '#F0D9B5' :'#B58863',
                          cursor: 'pointer',
                        }}
                      >
                        {isVM && !piece && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,0,0,0.25)' }} />
                          </div>
                        )}
                        {piece && (
                          <span style={{
                            color: piece.color === 'w' ? '#fff' : '#1a1a1a',
                            textShadow: piece.color === 'w' ? '0 1px 3px rgba(0,0,0,0.8)' : '0 1px 2px rgba(255,255,255,0.5)',
                            lineHeight: 1,
                          }}>
                            {PIECE_UNICODE[piece.type][piece.color]}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Move history */}
          {moveHistory.length > 0 && (
            <div className="mt-4 rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--muted-foreground)' }}>Recent Moves</p>
              <div className="flex flex-wrap gap-2">
                {moveHistory.map((m, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded"
                    style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>{m}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Leaderboard + stats */}
        <div className="space-y-4">
          <div className="rounded-2xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--muted)' }}>
                <p className="text-xl font-bold text-foreground">{moveCount}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Moves</p>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: 'var(--muted)' }}>
                <p className="text-xl font-bold" style={{ color: '#F59E0B' }}>{xp}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>XP</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Trophy size={16} style={{ color: '#F59E0B' }} /> Top Players
            </h3>
            <div className="space-y-3">
              {LEADERBOARD.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: i === 0 ? 'rgba(245,158,11,0.1)' : 'var(--muted)', border: `1px solid ${i === 0 ? 'rgba(245,158,11,0.3)' : 'transparent'}` }}>
                  <span className="text-base font-bold w-6 text-center"
                    style={{ color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#CD7F32' : 'var(--muted-foreground)' }}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{entry.name}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>ELO {entry.elo} · {entry.wins}W</p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: '#F59E0B' }}>{entry.score.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
